// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRight } from 'ramda';
import { useSnackbar } from 'notistack';
import moment from 'moment';

// css
import '../../../styles/announcementStyles.scss';

// MUI
import { Container, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { HourglassEmpty, PictureAsPdf } from '@mui/icons-material';

// Core
import { dayFormat } from '../../../../../core/constants';
import {
    ParameterValues,
    paramRepository,
    ResourceTextApplication,
} from '../../../../../core/resources';
import auth from '../../../../../core/authorization';

// Common
import {
    ADataGrid,
    ADataGridActions,
    AGridColumns,
    gridColumns,
} from '../../../../../common/dataGrid';
import {
    ADefaultIconButton,
    ADeleteIconButton,
    AEditIconButton,
    ASearchIconButton,
} from '../../../../../common/buttons';
import { TextsWithTooltip, TextWithTooltip } from '../../../../../common/typography';
import AAttachmentIconButton from '../../../../../common/buttons/AAttachmentIconButton';
import APriorityHighIconButton from '../../../../../common/buttons/APriorityHighIconButton';
import ADoneIconButton from '../../../../../common/buttons/ADoneIconButton';
import AWarningIconButton from '../../../../../common/buttons/AWarningIconButton';

// Interfaces
import {
    AnnouncementListDataGridColumnType,
    AnnouncementTypeEnum,
} from '../../../../../interfaces/enums';
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementAnimal,
    IAnimalAnnouncementBatch,
} from '../../../../../interfaces/IAnnouncement';
import { GUIDType } from '../../../../../interfaces/types';

// Store
// API
import animalAnnouncementApi from '../../../../../api/animalAnnouncementApi';
import chainInformationApi from '../../../../../api/chainInformationApi';
import producerReturnApi from '../../../../../api/producerReturnApi';

// Feature
import { IAnnouncementListDataGridProps } from '../../../../shared/types/propTypes';
import ConfirmDialog from '../../../../shared/components/confirm/ConfirmDialog';
import { AnimalAnnouncementDataGridStrings } from '../../../../shared/constants/animalAnnouncementDataGridStrings';
import { Strings } from '../../../../shared/constants/strings';
import {
    calculateAmountOfAnimals,
    calculateOrderedAnimalCount,
    collectWeekComparator,
    getAnnouncementAnimals,
    getUniqueCollectWeeks,
    handleAnimalAnnouncementDeletionRestrictions,
    isAnimalAnnouncementUpdateRestricted,
    hasAnnouncementResponseErrors,
} from '../../../helpers/data';
import ChainInformationDialog from '../../../../chain-information/components/ChainInformationDialog';
import { IChainInformationData } from '../../../../chain-information/IChainInformationDialog';
import { AnnouncementStatus } from '../../../../shared/types/announcementStatus';
import { GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useAppSelector } from '../../../../../store/hooks';
import { fetchPersonRelations } from '../../../../../persons/fetchPersonRelations';
import printingApi from '../../../../../api/printingApi';
import FormValidator from '../../../../../core/FormValidator';

// both announcements lists, sales and purchaces, in animal list view
export default function AnnouncementListDataGrid(props: IAnnouncementListDataGridProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaGeneral',
        'AnelmaTiltu',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const userData = useAppSelector((state) => state.userData.data);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<string>('');
    const [collectWeekDayParams, setCollectWeekDayParams] = useState<ParameterValues>([]);
    const [bovineSalesTypeParams, setBovineSalesTypeParams] = useState<ParameterValues>([]);
    const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>();
    const [chainInformationDialogVisible, setChainInformationDialogVisible] =
        useState<boolean>(false);
    const [announcementId, setAnnouncementId] = useState<GUIDType>('');
    const [announcementNumber, setAnnouncementNumber] = useState<string>('');
    const [animalsForChainInformation, setAnimalsForChainInformation] = useState<
        IAnimalAnnouncementAnimal[]
    >([]);
    const [chainInformations, setChainInformations] = useState<IChainInformationData[]>([]);
    const [isLoadingChainInfos, setIsLoadingChainInfos] = useState<boolean>(false);
    const [announcementIds, setAnnouncementIds] = useState<string[]>(props.data.map((i) => i.Id));
    const [snellmanUser, setSnellmanUser] = useState<boolean>(false);

    const openChainInformationDialog = (data: any) => {
        setAnnouncementId(data.Id);
        setAnnouncementNumber(data.Number);
        setAnimalsForChainInformation(getAnnouncementAnimals(data));
        setChainInformationDialogVisible(true);
    };

    useEffect(() => {
        if (userData) {
            fetchPersonRelations(userData.Logins[0].Login).then((response) => {
                setSnellmanUser(response[0]);
            });
        }
    }, [userData]);

    useEffect(() => {
        setIsLoadingChainInfos(true);
        setAnnouncementIds(props.data.map((i) => i.Id));
        setIsLoadingChainInfos(false);
    }, [props.data]);

    useEffect(() => {
        if (announcementIds.length > 0 && isLoadingChainInfos === false) {
            setIsLoadingChainInfos(true);
            chainInformationApi.loadMultipleChainInformations(announcementIds).then((response) => {
                if (response) {
                    let chainInfos: IChainInformationData[] = [];
                    for (let i = 0; i < response.Items.length; i++) {
                        let chainInfo: IChainInformationData = {
                            AnnouncementId: response.Items[i].AnnouncementId,
                            MedicatedFeed: response.Items[i].MedicatedFeed,
                            HealthInformation: response.Items[i].HealthInformation,
                            AuthorityRegulations: response.Items[i].AuthorityRegulations,
                            ForeignObject: response.Items[i].ForeignObject,
                            GmFree: response.Items[i].GmFree,
                            MedicalAccounting: response.Items[i].MedicalAccounting,
                            WaitingTimeOver: response.Items[i].WaitingTimeOver,
                            NasevaInfo: response.Items[i].NasevaInfo,
                            SlaughterHouseInfo: response.Items[i].SlaughterHouseInfo,
                            MedicationInfo: response.Items[i].MedicationInfo,
                            Anomalies: response.Items[i].Anomalies,
                            Regulations: response.Items[i].Regulations,
                            SamplingOrder: response.Items[i].SamplingOrder,
                            VetName: response.Items[i].VetName,
                            AdditionalInfo: response.Items[i].AdditionalInfo,
                            ForeignObjectInfo: response.Items[i].ForeignObjectInfo,
                            AnimalSymptoms: response.Items[i].Symptoms,
                            OtherSymptom: response.Items[i].OtherSymptom,
                            AnimalDiseases: response.Items[i].Diseases,
                            OtherDisease: response.Items[i].OtherDisease,
                            MedicatedAnimals: response.Items[i].Medications,
                            FormConfirmation: response.Items[i].FormConfirmation,
                            Signature: response.Items[i].Signature,
                            Signatory: response.Items[i].Signatory,
                            SignatureDate: response.Items[i].SignatureDate,
                        };
                        chainInfos.push(chainInfo);
                    }
                    setChainInformations(chainInfos);
                    setIsLoadingChainInfos(false);
                }
            });
        }
    }, [announcementIds, chainInformationDialogVisible]);

    useEffect(() => {
        if (
            props.canCreateAnimalAnnouncement !== null &&
            props.canCreateAnimalAnnouncement === false
        )
            enqueueSnackbar(t('AnelmaBovine:1093'), {
                variant: 'warning',
            });
    }, [props.canCreateAnimalAnnouncement]);

    const checkChainInfoFound = (announcementId: GUIDType) => {
        let chainInfoFound = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoFound) {
            return false;
        } else {
            return true;
        }
    };

    const checkChainInfoConfirmed = (announcementId: GUIDType) => {
        let chainInfoToCheck = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoToCheck?.FormConfirmation) {
            return true;
        } else {
            return false;
        }
    };

    const checkChainInfoNotify = (announcementId: GUIDType) => {
        let chainInfoToCheck = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoToCheck) {
            if (
                chainInfoToCheck.MedicatedFeed === true ||
                chainInfoToCheck.HealthInformation === true ||
                chainInfoToCheck.MedicalAccounting === true ||
                chainInfoToCheck.AuthorityRegulations === true ||
                chainInfoToCheck.ForeignObject === true ||
                chainInfoToCheck.GmFree === true ||
                chainInfoToCheck.AdditionalInfo.length > 0 ||
                chainInfoToCheck.MedicatedAnimals.length > 0
            ) {
                return true;
            } else {
                return false;
            }
        }
    };

    const checkChainInformationAuth = (announcementId: GUIDType) => {
        if (
            auth.canUpdateChainInformationAdmin === true &&
            checkChainInfoFound(announcementId) === false &&
            auth.canViewChainInformation === false
        ) {
            return false;
        } else if (
            auth.canUpdateChainInformationAdmin &&
            checkChainInfoFound(announcementId) === true
        ) {
            return false;
        } else if (auth.canViewChainInformation) {
            return false;
        } else {
            return true;
        }
    };

    const awaitingAnnouncementButton = (params: GridRenderCellParams) => (
        <ADefaultIconButton
            icon={
                <>
                    <HourglassEmpty />
                    {params.value && Number(params.value) > 0 ? params.value : ''}
                </>
            }
            tooltip={t('AnelmaBovine:1159')?.toString() ?? ''}
            onClick={() => {
                const announcementId = params.row.Id as GUIDType;
                animalAnnouncementApi.animalAnnouncementState(announcementId).then((res) => {
                    if (
                        res?.Entity.Status === AnnouncementStatus.Completed ||
                        res?.Entity.Status === AnnouncementStatus.Failed
                    ) {
                        props.handleAnnouncementReload(announcementId);
                    }
                });
            }}
            disabled={false}
        />
    );

    const getAwaitingAnnouncementButton = (params: GridRenderCellParams) => {
        const announcement = params.row as IAnimalAnnouncement;
        const value = announcement.Number;

        // empty cell for preliminary
        if (isPreliminaryAnnouncement(announcement)) {
            const preliminaryValue = announcement.PreliminaryNumber;

            if (!preliminaryValue)
                return '';

            return 'AL' + preliminaryValue;
        }

        var i = props.data.findIndex(a => a.Number === value);
        var hasUnknownBatchNumber = false;

        if (i !== -1) {
            hasUnknownBatchNumber = props.data[i].Batches.findIndex(b => b.BatchNumber === null) !== -1;
        }

        return value === 0 || params.row.Status === '1' || hasUnknownBatchNumber
            ? awaitingAnnouncementButton(params)
            : value;
    };

    const renderSalesTypes = (salesTypes: string[]) => {
        const initialSalesType = salesTypes[0];
        const tooltipSalesTypes = salesTypes.slice(1).join(', ');

        return (
            <>
                <Tooltip title={tooltipSalesTypes}>
                    <span>{initialSalesType}</span>
                </Tooltip>
            </>
        );
    };

    const getSalesTypes = (batches: IAnimalAnnouncementBatch[]) => {
        return batches.map((row: IAnimalAnnouncementBatch) => {
            return row.Animals.map((animal) => {
                return getSalesType(animal.SalesType, bovineSalesTypeParams);
            });
        });
    };

    const getRelatedSales = (purchase: IAnimalAnnouncement) => {
        let sales = props.getRelatedSales(purchase.Id);

        if (sales !== null) {
            // do not show when both are preliminary
            // and numbers are the same
            if (isPreliminaryAnnouncement(sales)
                && isPreliminaryAnnouncement(purchase)
                && sales.PreliminaryNumber === purchase.PreliminaryNumber)
                return null;

            return sales;
        }

        return null;
    }

    const renderRelatedSales = (relatedSales: IAnimalAnnouncement | null) => {
        if (relatedSales !== null) {
            if (isPreliminaryAnnouncement(relatedSales))
                return 'AL' + relatedSales.PreliminaryNumber;
            if (relatedSales.Number !== 0)
                return relatedSales.Number;
        }

        return '';
    }

    const defaultColumns: AGridColumns = [
        {
            field: AnimalAnnouncementDataGridStrings.AnnouncementNumber,
            headerName: t('AnelmaBovine:1137'),
            renderCell: getAwaitingAnnouncementButton,
            width: 80,
        },
        {
            field: AnimalAnnouncementDataGridStrings.TypeField,
            headerName: t('AnelmaBovine:1029'),
            renderCell: (params) => {
                const typeText = String(params.value);

                // AL.TE
                if (typeText.includes(t('AnelmaBovine:1222')))
                    return (
                        <Tooltip title={typeText.replace(t('AnelmaBovine:1222'), t('AnelmaBovine:1234'))}>
                            <Typography noWrap variant='body2'>
                                {typeText}
                            </Typography>
                        </Tooltip>
                    );

                // AL.VÄ
                if (typeText.includes(t('AnelmaBovine:1223')))
                    return (
                        <Tooltip title={typeText.replace(t('AnelmaBovine:1223'), t('AnelmaBovine:1235'))}>
                            <Typography noWrap variant='body2'>
                                {typeText}
                            </Typography>
                        </Tooltip>
                    ); 

                //Tuottajapalautus
                if (hasProducerReturns(params.row.Id))
                {
                    return (
                        <Tooltip title={t('AnelmaBovine:1237')}>
                            <span>{typeText}</span>
                        </Tooltip>
                    );
                }

                return (
                    <Tooltip title={typeText}>
                        <span>{typeText}</span>
                    </Tooltip>
                );
            },
            valueGetter: (params, row) => {    
                const baseType = resolveAnnouncementTypeAsText(row.Type);
                //AN-2773: Add "TP" indicator for slaughter announcements that have producer returns.
                return hasProducerReturns(row.Id) ? `${baseType} ${t('AnelmaBovine:1229')}` : baseType;
            },
            width: 130,
        },
        gridColumns.dateTime(
            AnimalAnnouncementDataGridStrings.CreatedField,
            t('AnelmaBovine:1138'),
            dayFormat,
            100
        ),
        {
            field: AnimalAnnouncementDataGridStrings.BatchesFields,
            headerName: t('AnelmaBovine:1139'),
            renderCell: (params) => {
                return <TextsWithTooltip texts={params.value as string[]} />;
            },
            valueGetter: (params, row) => {
                return getUniqueCollectWeeks(row.Batches as IAnimalAnnouncementBatch[]);
            },
            sortComparator: (x1, x2) =>
                collectWeekComparator(String((x1 as any[])[0]), String((x2 as any[])[0])),
            width: 130,
        },
        {
            field: AnimalAnnouncementDataGridStrings.ChainInformationField,
            headerName: t('AnelmaBovine:1067'),
            renderCell: (params) =>
                params.row.Type === AnnouncementTypeEnum.Slaughter.valueOf() ||
                params.row.Type === AnnouncementTypeEnum.ManualBovineSlaughter.valueOf() ||
                params.row.Type === AnnouncementTypeEnum.SlaughterWithChainInformation.valueOf() ? (
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                        <AAttachmentIconButton
                            disabled={
                                params.row.Number === 0 
                                    || isPreliminaryAnnouncement(params.row as IAnimalAnnouncement)
                                    || checkChainInformationAuth(params.row.Id)
                            }
                            key={`${params.id}-attachment`}
                            onClick={() => {
                                openChainInformationDialog(params.row);
                            }}
                            tooltip={t('AnelmaGeneral:1075')}
                        />
                        {checkChainInfoFound(params.row.Id) && (
                            <AWarningIconButton
                                onClick={() => {}}
                                tooltip={t('AnelmaBovine:1144')}
                            />
                        )}
                        {checkChainInfoConfirmed(params.row.Id) && (
                            <ADoneIconButton onClick={() => {}} tooltip={t('AnelmaBovine:1145')} />
                        )}
                        {checkChainInfoNotify(params.row.Id) && (
                            <APriorityHighIconButton
                                onClick={() => {}}
                                tooltip={t('AnelmaBovine:1146')}
                            />
                        )}
                    </Grid>
                ) : null,
            width: 120,
            sortable: false,
        },
        {
            field: AnimalAnnouncementDataGridStrings.HasFiles,
            headerName: t('AnelmaBovine:1038'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return row.HasFiles ? t('AnelmaBovine:1181') : t('AnelmaBovine:1182');
            },
            width: 70,
        },
        {
            field: AnimalAnnouncementDataGridStrings.AmountField,
            headerName: t('AnelmaBovine:1037'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return getAnimalCount(row.Batches, row.Type);
            },
            width: 110,
        },
        {
            field: AnimalAnnouncementDataGridStrings.SalesTypeField,
            headerName: t('AnelmaBovine:1019'),
            renderCell: (params) => renderSalesTypes(params.value as string[]),
            valueGetter: (params, row) => getSalesTypes(row.Batches as IAnimalAnnouncementBatch[]),
            width: 100,
        },
        gridColumns.singleSelectText(
            AnimalAnnouncementDataGridStrings.HoldingSiteField,
            [],
            t('AnelmaBovine:1140'),
            160
        ),
        gridColumns.singleSelectText(
            AnimalAnnouncementDataGridStrings.InfoField,
            [],
            t('AnelmaBovine:1039'),
            160
        ),
    ];

    const bovineIntermediationAnnouncementColumns: AGridColumns = [
        {
            field: AnimalAnnouncementDataGridStrings.AnnouncementNumber,
            headerName: t('AnelmaBovine:1137'),
            renderCell: getAwaitingAnnouncementButton,
            width: 80,
        },
        {
            field: AnimalAnnouncementDataGridStrings.TypeField,
            headerName: t('AnelmaBovine:1029'),
            renderCell: (params) => {
                let val = resolveAnnouncementTypeAsText(params.row.Type);

                // AL.TE
                if (val.includes(t('AnelmaBovine:1222')))
                    return (
                        <Tooltip title={val.replace(t('AnelmaBovine:1222'), t('AnelmaBovine:1234'))}>
                            <Typography noWrap variant='body2'>
                                {val}
                            </Typography>
                        </Tooltip>
                    );

                // AL.VÄ
                if (val.includes(t('AnelmaBovine:1223')))
                    return (
                        <Tooltip title={val.replace(t('AnelmaBovine:1223'), t('AnelmaBovine:1235'))}>
                            <Typography noWrap variant='body2'>
                                {val}
                            </Typography>
                        </Tooltip>
                    );

                return <TextWithTooltip text={val} />;
            },
            width: 120,
        },
        gridColumns.dateTime(
            AnimalAnnouncementDataGridStrings.CreatedField,
            t('AnelmaBovine:1138'),
            dayFormat,
            100
        ),
        {
            field: AnimalAnnouncementDataGridStrings.BatchesFields,
            headerName: t('AnelmaBovine:1139'),
            renderCell: (params) => {
                return <TextsWithTooltip texts={params.value as string[]} />;
            },
            valueGetter: (params, row) => {
                return getUniqueCollectWeeks(row.Batches as IAnimalAnnouncementBatch[]);
            },
            sortComparator: (x1, x2) =>
                collectWeekComparator(String((x1 as any[])[0]), String((x2 as any[])[0])),
            width: 120,
        },
        {
            field: AnimalAnnouncementDataGridStrings.AmountField,
            headerName: t('AnelmaBovine:1037'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return getAnimalCount(row.Batches, row.Type);
            },
            width: 110,
        },
        {
            field: AnimalAnnouncementDataGridStrings.SalesTypeField,
            headerName: t('AnelmaBovine:1019'),
            renderCell: (params) => renderSalesTypes(params.value as string[]),
            valueGetter: (params, row) => getSalesTypes(row.Batches as IAnimalAnnouncementBatch[]),
            width: 100,
        },
        gridColumns.singleSelectText(
            AnimalAnnouncementDataGridStrings.HoldingSiteField,
            [],
            t('AnelmaBovine:1140'),
            125
        ),
        gridColumns.singleSelectText(
            AnimalAnnouncementDataGridStrings.InfoField,
            [],
            t('AnelmaBovine:1039'),
            160
        ),
    ];

    const bovinePurchaseAnnouncementColumns: AGridColumns = [
        {
            field: AnimalAnnouncementDataGridStrings.AnnouncementNumber,
            headerName: t('AnelmaBovine:1028'),
            type: 'string',
            // Returns a string value for filtering/sorting: "AL<number>" for preliminary, "<number>" for regular, "" for pending
            valueGetter: (params, row) => {
                const announcement = row as IAnimalAnnouncement;
                if (isPreliminaryAnnouncement(announcement)) {
                    const preliminaryValue = announcement.PreliminaryNumber;
                    if (!preliminaryValue) return '';
                    return 'AL' + preliminaryValue;
                }
                if (announcement.Number === 0) return '';
                return String(announcement.Number);
            },
            renderCell: getAwaitingAnnouncementButton,
            // Custom sort: empty always last regardless of asc/desc direction.
            // "AL<number>" values sort by their numeric part, same as regular numbers.
            sortComparator: (v1, v2, cellParams1) => {
                const sortModel = cellParams1.api.getSortModel();
                const currentSort = sortModel.find((s: { field: string; sort: string | null | undefined }) => s.field === AnimalAnnouncementDataGridStrings.AnnouncementNumber);
                const directionMultiplier = currentSort?.sort === 'desc' ? -1 : 1;

                if (v1 === '' && v2 === '') return 0;
                if (v1 === '') return 1 * directionMultiplier;
                if (v2 === '') return -1 * directionMultiplier;

                const getNumeric = (v: unknown): number => {
                    const s = String(v);
                    const stripped = s.startsWith('AL') ? s.substring(2) : s;
                    const n = parseInt(stripped, 10);
                    return isNaN(n) ? 0 : n;
                };

                return getNumeric(v1) - getNumeric(v2);
            },
            width: 80,
        },
        {
            field: AnimalAnnouncementDataGridStrings.RelatedSaleAnnouncement,
            headerName: t('AnelmaBovine:1233'),
            type: 'string',
            valueGetter: (params, row) => {
                const relatedSales = getRelatedSales(row as IAnimalAnnouncement);
                return renderRelatedSales(relatedSales);
            },
            // Custom sort: empty always last regardless of asc/desc direction.
            // "AL<number>" values sort by their numeric part, same as regular numbers.
            sortComparator: (v1, v2, cellParams1) => {
                const sortModel = cellParams1.api.getSortModel();
                const currentSort = sortModel.find((s: { field: string; sort: string | null | undefined }) => s.field === AnimalAnnouncementDataGridStrings.RelatedSaleAnnouncement);
                const directionMultiplier = currentSort?.sort === 'desc' ? -1 : 1;

                // Empty always last regardless of sort direction
                if (v1 === '' && v2 === '') return 0;
                if (v1 === '') return 1 * directionMultiplier;
                if (v2 === '') return -1 * directionMultiplier;

                // Sort numerically: strip "AL" prefix if present, then compare as numbers
                const getNumeric = (v: unknown): number => {
                    const s = String(v);
                    const stripped = s.startsWith('AL') ? s.substring(2) : s;
                    const n = parseInt(stripped, 10);
                    return isNaN(n) ? 0 : n;
                };

                return getNumeric(v1) - getNumeric(v2);
            },
            width: 150,
        },
        {
            field: AnimalAnnouncementDataGridStrings.AmountField,
            headerName: t('AnelmaBovine:1037'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return getAnimalCount(row.Batches, row.Type);
            },
            width: 130,
        },
        {
            field: AnimalAnnouncementDataGridStrings.BatchesFields,
            headerName: t('AnelmaBovine:1127'),
            renderCell: (params) => {
                return <TextsWithTooltip texts={params.value as string[]} />;
            },
            valueGetter: (params, row) => {
                return getUniqueCollectWeeks(row.Batches as IAnimalAnnouncementBatch[]);
            },
            sortComparator: (x1, x2) =>
                collectWeekComparator(String((x1 as any[])[0]), String((x2 as any[])[0])),
            width: 110,
        },
        gridColumns.dateTime(
            AnimalAnnouncementDataGridStrings.CreatedField,
            t('AnelmaBovine:1033'),
            dayFormat,
            100
        ),
        {
            field: AnimalAnnouncementDataGridStrings.BatchInfoField,
            headerName: t('AnelmaBovine:1039'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => row.Batches[0].BatchInfo,
            width: 140,
        },
        gridColumns.singleSelectText(
            AnimalAnnouncementDataGridStrings.HoldingSiteField,
            [],
            t('AnelmaBovine:1063'),
            150
        ),
    ];

    useEffect(() => {
        paramRepository.load(['AnelmaAnimalAnnouncement', 'AnelmaBovine']).then(() => {
            setCollectWeekDayParams(
                paramRepository.resource('AnelmaAnimalAnnouncement', 'CollectWeekDay')
            );
            setBovineSalesTypeParams(paramRepository.resource('AnelmaBovine', 'SalesType'));
        });
    }, []);

    const handleConfirmDeleteDialogData = (id: string) => {
        setConfirmDialogVisible(true);
        setSelectedAnnouncement(id);
    };

    const deleteAnimalAnnouncement = async () => {
        setIsLoading(true);
        const returnsToDelete = props.producerReturns.filter(
            (r) => r.AnnouncementId === selectedAnnouncement
        );
        if (returnsToDelete.length > 0) {
            try {
                await Promise.all(returnsToDelete.map((r) => producerReturnApi.deleteProducerReturn(r.Id)));
                props.handleProducerReturnsDeletion(selectedAnnouncement);
            } catch {
                enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, { variant: 'error' });
                setIsLoading(false);
                return;
            }
        }
        const animalAnnouncement = props.data.find((a) => a.Id === selectedAnnouncement);
        if (animalAnnouncement !== undefined) {
            if (animalAnnouncement.Number && !isPreliminaryAnnouncement(animalAnnouncement)) {
                animalAnnouncementApi
                    .deleteAnimalAnnouncement(Number(animalAnnouncement.Number))
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                                variant: 'error',
                            });
                            setIsLoading(false);
                            return;
                        }
                        if (hasAnnouncementResponseErrors(response)) {
                            const errorCodes = Array.from(
                                new Set(response.Summary.Errors.map((_) => _.Code))
                            );
                            for (const errorCode of errorCodes) {
                                enqueueSnackbar(t(`AnelmaTiltu:${errorCode}`), {
                                    variant: 'error',
                                });
                            }
                            setIsLoading(false);
                            return;
                        }
                        setSelectedAnnouncement('');
                        props.handleAnnouncementDeletionFromTiltu(animalAnnouncement.Number);
                        setIsLoading(false);
                        const type = animalAnnouncement.Type;
                        if (
                            type !== AnnouncementTypeEnum.ManualBovineIntermediation &&
                            type !== AnnouncementTypeEnum.ManualBovineSlaughter
                        )
                            chainInformationApi.deleteChainInformation(selectedAnnouncement).then();
                    });
            } else {
                animalAnnouncementApi
                    .deleteAnimalAnnouncementById(animalAnnouncement.Id)
                    .then((response) => {
                        setSelectedAnnouncement('');
                        props.handleAnnouncementDeletionFromTiltuById(animalAnnouncement.Id);
                        setIsLoading(false);
                    })
                    .catch((err) => {
                        console.log('ERR', err);
                    });
            }
        }
    };

    const resolveAnnouncementTypeAsText = (type: AnnouncementTypeEnum): string => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
            case AnnouncementTypeEnum.SlaughterWithChainInformation:
                return t('AnelmaBovine:1070');
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
                return t('AnelmaBovine:1071');
            case AnnouncementTypeEnum.BovinePurchase:
            case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                return t('AnelmaBovine:1072');
            case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                return t('AnelmaBovine:1222');
            case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                return t('AnelmaBovine:1223');
        }
    };

    const resolveEditTooltipText = (type: AnnouncementTypeEnum): string => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
                return t('AnelmaBovine:1044');
            default:
                return t('AnelmaGeneral:1075');
        }
    };

    const getSalesType = (salesType: number, bovineSalesTypeParams: ParameterValues) => {
        return salesType > 0
            ? bovineSalesTypeParams.find((_) => _.code === String(salesType))?.text
            : '';
    };

    const getAnimalCount = (
        batches: IAnimalAnnouncementBatch[],
        announcementType: AnnouncementTypeEnum
    ): number => {
        switch (announcementType) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
            case AnnouncementTypeEnum.SlaughterWithChainInformation:
            case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                return calculateAmountOfAnimals(batches);
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
            case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                return calculateAmountOfAnimals(batches);
            case AnnouncementTypeEnum.BovinePurchase:
            case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                return calculateOrderedAnimalCount(batches);
        }
    };

    const hasProducerReturns = (announcementId: GUIDType): boolean => {
        return props.producerReturns.some((r) => r.AnnouncementId === announcementId);
    };

    const handleEditBovineAnnouncement = (item: IAnimalAnnouncement) => {
        props.handleModification(item as IAnimalAnnouncement, checkChainInfoFound(item.Id));
    };

    const handleAnimalAnnouncementUpdateBtnDisabled = (rowStatus: string) => {
        if (props.canCreateAnimalAnnouncement !== null && !props.canCreateAnimalAnnouncement)
            return true;
        if (rowStatus === '1') return true;
    };

    const toggleAnnouncementReadOnlyMode = (params: GridRenderCellParams): JSX.Element => {
        if (isAnimalAnnouncementUpdateRestricted(params.row as IAnimalAnnouncement))
            return (
                <ASearchIconButton
                    key={`${params.id}-edit`}
                    onClick={() => {
                        let item = params.row as unknown as IAnimalAnnouncement;
                        handleEditBovineAnnouncement(item);
                    }}
                    tooltip={t('AnelmaBovine:1163')}
                    disabled={
                        (params.row.Number === 0
                            && (params.row as IAnimalAnnouncement).Type !== AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement)
                        || handleAnimalAnnouncementUpdateBtnDisabled(params.row.Status)
                    }
                />
            );

        return (
            <AEditIconButton
                key={`${params.id}-edit`}
                onClick={() => {
                    let item = params.row as unknown as IAnimalAnnouncement;
                    handleEditBovineAnnouncement(item);
                }}
                tooltip={resolveEditTooltipText(params.row.Type)}
                disabled={handleAnimalAnnouncementUpdateBtnDisabled(params.row.Status)}
            />
        );
    };

    const dataGridActions: ADataGridActions = [
        (params) => {
            {
                return (
                    auth.canDeleteAnelmaBovineBrokerPurchase && (
                        <ADeleteIconButton
                            key={`${params.id}-delete`}
                            onClick={() => {
                                handleConfirmDeleteDialogData(String(params.id));
                            }}
                            tooltip={t('AnelmaGeneral:1012')}
                            disabled={handleAnimalAnnouncementDeletionRestrictions(
                                params.row as IAnimalAnnouncement,
                                snellmanUser,
                                props.producerReturns.findIndex(r => r.AnnouncementId === (params.row as IAnimalAnnouncement).Id) != -1
                            )}
                        />
                    )
                );
            }
        },
        (params) => {
            {
                return (
                    auth.canUpdateAnelmaBovineBrokerPurchase &&
                    toggleAnnouncementReadOnlyMode(params)
                );
            }
        },
        (params) => {
            let canPrint =
                auth.canViewAnelmaBovineBrokerPurchase &&
                (params.row as IAnimalAnnouncement).Number !== 0 &&
                !isPreliminaryAnnouncement(params.row as IAnimalAnnouncement) &&
                auth.canViewBovineSlaughterAnnouncementDialog;

            return (
                <ADefaultIconButton
                    icon={<PictureAsPdf />}
                    onClick={() => {
                        printingApi.printAnimalAnnouncement(
                            (params.row as IAnimalAnnouncement).Number
                        );
                    }}
                    tooltip=''
                    disabled={!canPrint}
                />
            );
        },
    ];

    const isPreliminaryAnnouncement = (announcemeent: IAnimalAnnouncement) => {
        return announcemeent.Type == AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
            || announcemeent.Type == AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            || announcemeent.Type == AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement;
    }

    const getRowColourClass = (row: IAnimalAnnouncement) => {
        if (isPreliminaryAnnouncement(row)) {
            if (row.ScheduledFrom == null
                || moment(row.ScheduledFrom) < moment())
                return 'preliminary-announcement-to-confirm';
        }

        return '';
    }

    const toggleChainInformationReadOnly = (announcementId: GUIDType): boolean => {
        if (announcementId) {
            const selectedAnnouncement = props.data.find((_) => _.Id === announcementId);
            if (selectedAnnouncement !== undefined)
                return isAnimalAnnouncementUpdateRestricted(selectedAnnouncement);
        }
        return false;
    };

    const getDeleteConfirmationContent = () => {
        const hasProducerReturn = props.producerReturns.some(
            (r) => r.AnnouncementId === selectedAnnouncement
        );

        if (hasProducerReturn) {
            return {
                title: 'AnelmaAnimalAnnouncement:1039',
                content: 'AnelmaAnimalAnnouncement:1038',
            };
        }
        return {
            title: 'AnelmaGeneral:1142',
            content: 'AnelmaGeneral:1012',
        };
    };
    
    const collectWeekSort = (a: IAnimalAnnouncement, b: IAnimalAnnouncement) => {
        let sa = getUniqueCollectWeeks(a.Batches);
        let sb = getUniqueCollectWeeks(b.Batches);

        return collectWeekComparator(sa[0], sb[0]);
    }

    return (
        <Container id={Strings.AnnouncementListGridContainer}>
            <ADataGrid
                disableSelectionOnClick={true}
                loading={isLoading}
                actions={dataGridActions}
                columns={
                    props.columnType === AnnouncementListDataGridColumnType.Default
                        ? defaultColumns
                        : props.columnType ===
                            AnnouncementListDataGridColumnType.BovineIntermediation
                            ? bovineIntermediationAnnouncementColumns
                            : bovinePurchaseAnnouncementColumns
                }
                enablePagination
                rows={props.data
                    .map((i) =>
                        mergeRight(i, {
                            id: i.Id,
                        })
                )
                    .sort((a, b) => collectWeekSort(a as IAnimalAnnouncement, b as IAnimalAnnouncement))}
                onSelectionModelChange={(selectionModel) => {
                    setSelectedRow(selectionModel);
                    props.handleSelectedAnnouncement(selectionModel);
                }}
                enableFiltering
                getRowClassName={(params) => getRowColourClass(params.row as IAnimalAnnouncement)}
            />

            {confirmDialogVisible && (
                <ConfirmDialog
                    title={getDeleteConfirmationContent().title}
                    content={getDeleteConfirmationContent().content}
                    actionButtonType={Strings.AnnouncementDeleteConfirmActionType}
                    onClose={() => setConfirmDialogVisible(false)}
                    onConfirm={() => deleteAnimalAnnouncement()}
                />
            )}
            {chainInformationDialogVisible && (
                <ChainInformationDialog
                    onClose={() => {
                        setChainInformationDialogVisible(false);
                    }}
                    announcementData={null}
                    announcementId={announcementId}
                    announcementNumber={announcementNumber}
                    animals={animalsForChainInformation}
                    forceReadOnly={toggleChainInformationReadOnly(announcementId)}
                    validator={new FormValidator()}
                    medicationNumberFieldValidator={new FormValidator()}
                />
            )}
        </Container>
    );
}
